import {Inject, Injectable} from '@angular/core';
import {SessionStorageService} from 'app/core/services/session-storage.service';
import {AuthService, CurrentUser} from 'app/core/services/auth.service';
import {AppUrls, path} from 'app/core/url/app-urls.service';

import {isBackPathAllowed, Redirect, REDIRECT} from 'app/core/helpers/redirect';
import {Sentry} from 'app/core/error-handler/sentry.service';
import {delay} from 'rxjs/operators';
import {UserRoles} from 'app/core/helpers/user.roles';

const backUrlRedirectLimit = 3;
const incrementBackUrlRedirectCountKey = 'apd_back_url_redirect_count';

@Injectable({
    providedIn: 'root',
})
export class NavigateAfterLoginService {
    constructor(
        private sessionStorage: SessionStorageService,
        @Inject(REDIRECT) private redirect: Redirect,
        private sentry: Sentry,
        private auth: AuthService
    ) {}

    getSentryTimeToSendMs() {
        return 1000;
    }

    getBackPath(): string {
        const backToPath = extractBackToPath(this.getLocation());

        if (backToPath && isBackPathAllowed(backToPath)) {
            return backToPath;
        }
        return '';
    }

    getLocation(): Location {
        return location;
    }

    async navigate(user?: CurrentUser) {
        const backToPath = this.getBackPath();

        if (user?.onboardingShowCreateApp) {
            return this.redirect('/onboarding/app');
        }

        if (backToPath) {
            // to prevent cycle redirect after navigation if some auth issues here
            const cycleCount = this.incrementBackUrlRedirectCount();
            console.debug(`[NavigateAfterLoginService] Navigate to back url, redirect Cycles count: ${cycleCount} path: ${backToPath}`);
            this.sentry.leaveBreadCrumbs({
                category: 'NavigateAfterLoginService',
                message: `Navigate to back url, redirect Cycles count: ${cycleCount} path: ${backToPath}`,
            });
            if (cycleCount > backUrlRedirectLimit) {
                console.debug(`[NavigateAfterLoginService] Navigate to back url limit reached. Do default redirect`);
                // to be sure user is set
                this.sentry.setUserContext(user);
                this.sentry.handleError(new Error('NavigateAfterLoginService: Cycle redirect detected'));
                // sleep 1 sec to be sure sentry get error notification.
                // then signout and redirect to sign in with NO back url
                return this.auth
                    .signOut()
                    .pipe(delay(this.getSentryTimeToSendMs()))
                    .toPromise()
                    .then(() => this.redirect(path(AppUrls.SIGN_IN)));
            }

            return this.redirect(backToPath);
        }

        this.navigateToRoot(user);
    }

    navigateToRoot(user?: CurrentUser) {
        if ((user?.roles || []).includes(UserRoles.NetworkPartner)) {
            return this.redirect('/analytics/reports');
        }

        if (user?.permissions?.canViewInternalDashboard) {
            return this.redirect('/analytics/internal');
        }

        return this.redirect('/analytics/overview');
    }

    flushBackUrlRedirectCount() {
        this.sessionStorage.removeItem(incrementBackUrlRedirectCountKey);
    }

    incrementBackUrlRedirectCount(): number {
        let count: number;
        try {
            count = JSON.parse(this.sessionStorage.getItem(incrementBackUrlRedirectCountKey));
        } catch (e) {
            console.debug(e);
        }
        if (isNaN(count) || !(typeof count === 'number') || count < 0) {
            count = 0;
        }
        count++;
        this.sessionStorage.setItem(incrementBackUrlRedirectCountKey, String(count));
        return count;
    }
}

export function extractBackToPath(location: Location): string | null {
    const params = new URLSearchParams(location.search);
    const backToPath = params.get('back_to');
    if (!backToPath) {
        return;
    }

    const url = new URL(backToPath, location.origin);
    if (url.origin !== location.origin) {
        return;
    }

    return url.pathname + url.search + url.hash;
}
